import { useContext } from 'react';
import profileContext from '@wlb/shared/context/ProfileContext';
import { isPlaceholderProfilePictureUrl } from '@wlb/shared/utils/imageUrl';
import type { Profile } from '@wlb/shared/types';

/**
 * Reads the signed-in user's profile without requiring <ProfileProvider>.
 *
 * The provider is only mounted for authenticated visitors, because its mount
 * effect calls GET /UserProfile/me and an anonymous 401 would trigger the
 * session-expired flow.
 */
export function useCurrentProfile(): Profile | null {
  return useContext(profileContext)?.profile ?? null;
}

/**
 * Resolves the avatar to a plain URL; bundled numeric assets have none on web.
 * Known API placeholders resolve to '' so callers fall back to initials.
 */
export function getProfileAvatarUrl(profile: Profile | null): string {
  const avatar = profile?.avatar;
  if (!avatar || typeof avatar === 'number') return '';
  const url =
    typeof avatar === 'string'
      ? avatar
      : ((Array.isArray(avatar) ? avatar[0] : avatar)?.uri ?? '');
  return isPlaceholderProfilePictureUrl(url) ? '' : url;
}
